import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqs = [
    {
      question: "Peut-on tester avant de s’engager ?",
      answer:
        "Oui. Nous proposons un audit IA gratuit pour identifier rapidement les opportunités à ROI et valider le potentiel avant déploiement."
    },
    {
      question: "Combien coûte une stratégie marketing IA ?",
      answer:
        "Le coût dépend du périmètre et des objectifs. Nos clients observent généralement un ROI positif dès les premiers mois."
    },
    {
      question: "En combien de temps peut-on obtenir des résultats ?",
      answer:
        "Des premiers résultats sont visibles en quelques semaines. Les gains significatifs apparaissent entre 1 et 3 mois."
    },
    {
      question: "Quels résultats pouvez-vous obtenir ?",
      answer:
        "Amélioration du ROI, augmentation des leads qualifiés et réduction du coût d’acquisition grâce à l’IA."
    },
    {
      question: "Quelle est votre différence avec une agence classique ?",
      answer:
        "Nous combinons stratégie, IA et exécution avec une approche orientée performance et automatisation."
    },
    {
      question: "Est-ce adapté aux PME ?",
      answer:
        "Oui. Nos solutions sont conçues pour être simples, efficaces et rapidement rentables."
    },
    {
      question: "Comment l’IA peut-elle générer plus de leads ?",
      answer:
        "Grâce à un ciblage précis, une personnalisation avancée et des campagnes automatisées."
    }
  ];

  const scrollToContact = () => {
    const el = document.getElementById("contact");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.section
      id="faq"
      className="py-24 bg-black"
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      viewport={{ once: true }}
    >
      <div className="container mx-auto px-4">

        {/* HEADER */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold mb-4">
            FAQ Marketing IA : coûts, ROI et stratégies
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Comprenez comment l’IA améliore votre marketing et accélère votre croissance
          </p>
        </div>

        {/* FAQ */}
        <div className="max-w-3xl mx-auto space-y-4">
          {faqs.map((faq, index) => {
            const isOpen = openIndex === index;

            return (
              <motion.div
                key={index}
                layout
                transition={{ type: "spring", stiffness: 120, damping: 20 }}
                className={`
                  rounded-xl border
                  transition-all duration-300
                  ${
                    isOpen
                      ? "border-blue-500/40 bg-[#0B0F19] shadow-[0_15px_30px_rgba(59,130,246,0.15)]"
                      : "border-gray-800 bg-[#0B0F19]"
                  }
                `}
              >
                {/* QUESTION */}
                <button
                  onClick={() => setOpenIndex(isOpen ? null : index)}
                  className="w-full text-left p-6 flex items-center justify-between"
                >
                  <span className="text-lg font-semibold text-white">
                    {faq.question}
                  </span>

                  <motion.div
                    animate={{ rotate: isOpen ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <ChevronDown className="w-5 h-5 text-blue-400" />
                  </motion.div>
                </button>

                {/* ANSWER */}
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      key="content"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-6 text-gray-300 leading-relaxed">
                        {faq.answer}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <button
            onClick={scrollToContact}
            className="
              bg-blue-500
              hover:bg-blue-600
              text-white
              px-8 py-4
              rounded-lg
              text-lg
              font-semibold
              transition-all duration-300
              hover:scale-105
            "
          >
            Demander un audit IA gratuit
          </button>
        </div>

      </div>
    </motion.section>
  );
}